import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { HighlightArea } from '@react-pdf-viewer/highlight';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';

import {
  ChevronIcon,
  CompareDuplicateIcon,
  ExitIcon,
  MissingSignatureIcon,
  RemoveTagIcon,
  SummaryIcon,
} from '@/assets/icons';
import { DataRoomClientFile } from '@/common/types';
import LoadingSpinner from '@/components/LoadingSpinner';
import { useDataRoom } from '@/contexts/overview/dataroom/utils';
import { MarveriIcon } from '@/pages/overview/common/MarveriIcon';
import { ScrollableDiv } from '@/pages/overview/common/ScrollableDiv';
import { DocumentAnalysisTab } from '@/pages/overview/common/side-panel/DocumentAnalysisTab';
import { ReferencesTab } from '@/pages/overview/common/side-panel/ReferencesTab';
import { SidePanelAddTool } from '@/pages/overview/common/side-panel/SidePanelAddTool';
import { TertiaryMarveriButton } from '@/pages/overview/common/TertiaryMarveriButton';
import { useOverview } from '@/pages/overview/common/utils';
import { SelectedFolderDisplay } from '@/pages/overview/dataroom/sidebar/SelectedFolderDisplay';
import { Signatory } from '@/pages/pdf-viewer/PdfViewer';
import { trpcClient, trpcReact } from '@/utils/trpc';

interface RightSidePanelProps {
  pdfInteraction: boolean;
  hoveredHighlight?: HighlightArea | null;
  sidePanelTabIndex: number;
  handleJumpToHighlight?: (highlights: number[]) => void;
  handleReferenceMouseOver?: (highlights: number[]) => void;
}

interface RightSidePanelTabProps {
  label: string;
  indicator?: string;
  setSelectedTabIndex: () => void;
}

const RightSidePanelTab = ({ label, indicator, setSelectedTabIndex }: RightSidePanelTabProps) => {
  const { checkForTool } = useOverview();

  return (
    <Tab as="div" className="outline-none">
      {({ selected }) => (
        <div
          className={`flex cursor-pointer items-center justify-start gap-1 border-b-2 px-3 py-[9px] ${
            selected ? ' border-[#e9e5ea]' : 'border-transparent'
          }`}
          onClick={setSelectedTabIndex}
        >
          <div
            className={` flex items-center gap-1 font-['Inter'] text-sm font-medium hover:text-[#e9e5ea] ${
              selected ? 'text-[#e9e5ea]' : 'text-[#a8a5a9]'
            }`}
          >
            {label}
            {checkForTool('MISSING_DOCUMENT') && indicator && (
              <span className="flex h-[15px] w-[20px] items-center justify-center rounded-[28px] bg-N400 text-center text-marveri-white">
                {indicator}
              </span>
            )}
          </div>
        </div>
      )}
    </Tab>
  );
};

export const RightSidePanel = ({
  pdfInteraction,
  hoveredHighlight,
  sidePanelTabIndex,
  handleJumpToHighlight,
  handleReferenceMouseOver,
}: RightSidePanelProps) => {
  const {
    selectedFile,
    setSelectedFile,
    selectedFileDetails,
    checkedFiles,
    selectedFoldersArray,
    setSidePanelTabIndex,
    setIsSidePanelOpen,
    setIsDualViewerOpen,
    selectSecondFileByName,
    setIsViewerOpen,
    dataRoomFiles,
  } = useDataRoom();
  const { checkForTool, matter } = useOverview();
  const summaryDivRef = useRef<HTMLDivElement>(null);
  const signatureSectionRef = useRef<HTMLDivElement>(null);
  const [isAddingSignaturesTool, setIsAddingSignaturesTool] = useState(false);
  const [isAddingSummaryTool, setIsAddingSummaryTool] = useState(false);
  const [isSignatureSectionOpen, setIsSignatureSectionOpen] = useState(false);
  const [signatureSectionHeight, setSignatureSectionHeight] = useState(0);

  const flags = useFlags();

  const addToolFunction = trpcReact.clientMatter.addTool.useMutation();

  const calculateSummaryHeight = useCallback(() => {
    if (selectedFileDetails?.summary) {
      if (checkForTool('MISSING_SIGNATURE')) {
        if (selectedFileDetails?.missingSignature == null) {
          if (pdfInteraction) {
            return { height: 'calc(100vh - 425px)' };
          }
          return { height: 'calc(100vh - 390px)' };
        }
        if (isSignatureSectionOpen) {
          if (pdfInteraction) {
            return { height: `calc(100vh - ${signatureSectionHeight + 315}px)` };
          }
          return { height: `calc(100vh - ${signatureSectionHeight + 280}px)` };
        }
        if (pdfInteraction) {
          return { height: 'calc(100vh - 380px)' };
        }
        return { height: 'calc(100vh - 345px)' };
      }
      if (pdfInteraction) {
        return { height: 'calc(100vh - 460px)' };
      }
      return { height: 'calc(100vh - 425px)' };
    }
    return { height: '0' };
  }, [
    selectedFileDetails?.summary,
    selectedFileDetails?.missingSignature,
    checkForTool,
    pdfInteraction,
    isSignatureSectionOpen,
    signatureSectionHeight,
  ]);

  useEffect(() => {
    if (
      checkForTool('MISSING_SIGNATURE') &&
      selectedFileDetails?.missingSignature !== null &&
      !isAddingSignaturesTool
    ) {
      if (signatureSectionRef.current) {
        const measuredHeight = signatureSectionRef.current.offsetHeight;
        setSignatureSectionHeight(measuredHeight);
      }
    }
  }, [
    checkForTool,
    isAddingSignaturesTool,
    isSignatureSectionOpen,
    selectedFileDetails?.missingSignature,
    signatureSectionHeight,
  ]);

  useEffect(() => {
    if (selectedFile) {
      if (summaryDivRef.current) {
        summaryDivRef.current.scrollTop = 0;
      }
    }
  }, [selectedFile]);

  useEffect(() => {
    const savedSidePanelTab = localStorage.getItem('document-list-side-panel-tab');
    if (savedSidePanelTab) {
      setSidePanelTabIndex(parseInt(savedSidePanelTab));
    } else {
      setSidePanelTabIndex(sidePanelTabIndex);
    }
  }, [setSidePanelTabIndex, sidePanelTabIndex]);

  useEffect(() => {
    if (sidePanelTabIndex === 0) {
      setIsSignatureSectionOpen(true);
    }
  }, [sidePanelTabIndex]);

  if (
    (selectedFoldersArray.length !== 0 || checkedFiles.length !== 0) &&
    selectedFile === undefined
  ) {
    return <SelectedFolderDisplay />;
  }

  const formatUploadTime = (createdAt: string) => {
    const date = new Date(createdAt);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const visibleTabs = [true, true, flags.notesFeature].filter(Boolean).length;

  const handleClosePanel = () => {
    setSidePanelTabIndex(0);
    setSelectedFile('');
    setIsSidePanelOpen(false);
    localStorage.removeItem('document-list-side-panel-tab');
    localStorage.removeItem('document-list-file-id');
  };

  const handleSidePanelTabChange = (tabIndex: number) => {
    localStorage.setItem('document-list-side-panel-tab', tabIndex.toString());
    setSidePanelTabIndex(tabIndex);
  };

  const handleSignatoryClick = (signatory: Signatory) => {
    if (signatory.page === 0) return;

    const signatoryPage = signatory.page - 1;
    if (pdfInteraction && handleJumpToHighlight) {
      handleJumpToHighlight([signatory.page, 0, 0, 0, 0]);
    } else {
      localStorage.setItem('current-page', `${signatoryPage}`);
      setIsViewerOpen(true);
    }
  };

  const handleDeleteDuplicate = (duplicateFile: DataRoomClientFile) => {
    if (selectedFileDetails) {
      const remainingDuplicates = dataRoomFiles.filter(
        (file) =>
          file.duplicateCluster === selectedFileDetails.duplicateCluster &&
          file.id !== selectedFileDetails.id &&
          file.id !== duplicateFile.id, // exclude the one we're about to remove
      );

      // 1. Remove this file from the cluster
      trpcClient.dataRoom.updateDuplicateCluster.mutate({
        clientMatterId: matter.id,
        fileId: duplicateFile.id,
        duplicateCluster: null,
      });

      // 2. If this was the last duplicate, remove the original from the cluster too
      if (remainingDuplicates.length === 0) {
        trpcClient.dataRoom.updateDuplicateCluster.mutate({
          clientMatterId: matter.id,
          fileId: selectedFileDetails.id,
          duplicateCluster: null,
        });
      }
    }
  };

  const handleCompareDuplicate = (duplicateFile: DataRoomClientFile) => {
    selectSecondFileByName(duplicateFile.name);
    setIsViewerOpen(true);
    setIsDualViewerOpen(true);
  };

  const handleDuplicateClick = (fileName: string, fileId: string) => {
    setSelectedFile(fileName);
    localStorage.setItem('document-list-file-id', fileId);
    handleSidePanelTabChange(0);
  };

  const handleAddSignaturesTool = () => {
    setIsAddingSignaturesTool(true);
    addToolFunction.mutate(
      {
        clientNumber: matter.client.number,
        number: matter.number,
        tool: 'MISSING_SIGNATURE',
        clientMatterId: matter.id,
      },
      {
        onSettled: () => {
          setIsAddingSignaturesTool(false);
        },
      },
    );
  };

  const SUMMARY_TOOLS_TO_CHECK = [
    'RENAME',
    'ORGANIZE',
    'MISSING_DOCUMENT',
    'FINANCE_REVIEW',
    'CONTRACT_CLAUSES',
    'PERSONNEL_REPORT',
    'COMPANY_OVERVIEW',
    'COMPUTE_SUMMARY',
    'MISSING_SIGNATURE',
  ];

  const anySummaryToolRunning = SUMMARY_TOOLS_TO_CHECK.some((tool) => checkForTool(tool));

  const handleAddSummaryTool = () => {
    setIsAddingSummaryTool(true);
    addToolFunction.mutate(
      {
        clientNumber: matter.client.number,
        number: matter.number,
        tool: 'COMPUTE_SUMMARY',
        clientMatterId: matter.id,
      },
      {
        onSettled: () => {
          setIsAddingSummaryTool(false);
        },
      },
    );
  };

  const renderDuplicateDocumentsSection = () => {
    if (!selectedFileDetails || !selectedFileDetails.duplicateCluster) return null;

    const duplicates = dataRoomFiles.filter(
      (file) =>
        file.duplicateCluster === selectedFileDetails.duplicateCluster &&
        file.id !== selectedFileDetails.id,
    );

    return (
      <section className="py-3" id="duplicate-files-section">
        <div className="mb-4 flex h-[36px] w-full items-center justify-center rounded-[4px] bg-N500 pr-2 text-center text-[14px] font-medium text-marveri-white">
          Possible Duplicates
        </div>
        <div className="inline-flex w-full flex-col items-start justify-start gap-3 py-3">
          {duplicates.map((duplicateFile, idx) => (
            <div key={idx + 'duplicate'} className="flex w-full items-center justify-between">
              <div
                className="max-w-[275px] cursor-pointer font-['Inter'] text-[14px] font-medium text-[#e9e5ea] hover:underline"
                onClick={() => handleDuplicateClick(duplicateFile.name, duplicateFile.id)}
              >
                {duplicateFile.displayName}
              </div>
              <div className="flex items-center justify-end gap-2">
                <TertiaryMarveriButton
                  icon={CompareDuplicateIcon}
                  label="Compare"
                  onClick={() => handleCompareDuplicate(duplicateFile)}
                />
                <TertiaryMarveriButton
                  icon={RemoveTagIcon}
                  label="Remove duplicate tag"
                  onClick={() => handleDeleteDuplicate(duplicateFile)}
                />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  };

  return (
    <>
      {selectedFileDetails && (
        <div
          className={`relative flex h-full w-[420px] shrink-0 flex-col bg-container-default pl-4 pr-1 text-marveri-white`}
          data-testid="right-panel-sidebar"
        >
          <TabGroup defaultIndex={sidePanelTabIndex} className="flex h-full flex-col" manual>
            {!pdfInteraction && (
              <div className="flex items-end justify-end">
                <MarveriIcon
                  icon={ExitIcon}
                  iconStyle=""
                  iconType="other"
                  onClick={handleClosePanel}
                />
              </div>
            )}
            <TabList className={`${pdfInteraction ? 'mt-[15px]' : 'mt-[5px]'}`}>
              <div
                className={`${visibleTabs > 1 ? 'justify-between' : 'justify-center'} m-auto mb-6 flex h-9 w-full justify-center px-2 font-medium`}
              >
                <RightSidePanelTab
                  label="Overview"
                  setSelectedTabIndex={() => handleSidePanelTabChange(0)}
                />
                <RightSidePanelTab
                  label="References"
                  indicator={`${selectedFileDetails.references.length}`}
                  setSelectedTabIndex={() => handleSidePanelTabChange(1)}
                />
                {flags.documentAnalysis && (
                  <RightSidePanelTab
                    label="Document Analysis"
                    setSelectedTabIndex={() => handleSidePanelTabChange(2)}
                  />
                )}
                {flags.notesFeature && (
                  <RightSidePanelTab
                    label="Notes"
                    setSelectedTabIndex={() => handleSidePanelTabChange(3)}
                  />
                )}
              </div>
            </TabList>
            <TabPanels className="h-full ">
              <TabPanel className=" h-full ">
                <div className={'flex h-full flex-col justify-between'}>
                  <section className="relative flex h-full flex-col">
                    {/* File Info Section */}
                    <div className="mb-4 space-y-2">
                      {selectedFileDetails.originalPathname &&
                        (() => {
                          const filename = selectedFileDetails.originalPathname
                            .replace(/\/+$/, '')
                            .split('/')
                            .pop();
                          return (
                            filename && (
                              <div>
                                <h3 className="text-[12px] font-bold text-marveri-light-silver">
                                  ORIGINAL FILENAME
                                </h3>
                                <p className="break-all text-[14px] text-[#e9e5ea]">{filename}</p>
                              </div>
                            )
                          );
                        })()}
                      {selectedFileDetails.createdAt && (
                        <div>
                          <h3 className="text-[12px] font-bold text-marveri-light-silver">
                            UPLOAD TIME
                          </h3>
                          <p className="text-[14px] text-[#e9e5ea]">
                            {formatUploadTime(selectedFileDetails.createdAt)}
                          </p>
                        </div>
                      )}
                    </div>
                    <div
                      className={`${!selectedFileDetails.summary && !selectedFileDetails.missingSignature ? '' : 'justify-between'} flex h-full flex-col`}
                    >
                      <div>
                        <div className="top-0 mb-4 flex h-[36px] w-full items-center justify-center rounded-[4px] bg-N600 pr-2 text-center text-[14px] font-medium text-marveri-white">
                          Summary
                        </div>
                        {anySummaryToolRunning ? (
                          <>
                            {!selectedFileDetails.summary ? (
                              <>
                                <div className="flex items-center justify-center gap-2">
                                  <LoadingSpinner />
                                  <span className="text-[16px]">Computing Summary...</span>
                                </div>
                                {renderDuplicateDocumentsSection()}
                              </>
                            ) : (
                              <ScrollableDiv
                                containerStyle={`${
                                  pdfInteraction ? 'min-h-[80px]' : 'min-h-[150px]'
                                } pr-2 font-normal scrollbar-thumb-[#403E40] text-[14px]`}
                                extraComponentStyling={calculateSummaryHeight()}
                                ref={summaryDivRef}
                                scrollKey="right-side-panel-summary"
                              >
                                <Markdown
                                  remarkPlugins={[remarkBreaks]}
                                  // @ts-expect-error markdown expects non-generic elements
                                  components={{
                                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                    h1: ({ node, ...props }) => (
                                      <h2
                                        className="font-['Inter'] font-semibold leading-tight text-[#e9e5ea]"
                                        {...props}
                                      />
                                    ),
                                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                    h2: ({ node, ...props }) => (
                                      <h2
                                        className="mb-2 mt-3 font-['Inter'] font-medium leading-tight text-[#e9e5ea]"
                                        {...props}
                                      />
                                    ),
                                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                    h3: ({ node, ...props }) => (
                                      <h2
                                        className="font-['Inter'] font-semibold leading-tight text-[#e9e5ea]"
                                        {...props}
                                      />
                                    ),
                                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                    h4: ({ node, ...props }) => (
                                      <h2
                                        className="font-['Inter'] font-medium leading-tight text-[#e9e5ea]"
                                        {...props}
                                      />
                                    ),
                                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                    p: ({ node, ...props }) => (
                                      <p
                                        className="font-['Inter'] font-normal leading-tight text-[#e9e5ea]"
                                        {...props}
                                      />
                                    ),
                                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                    ul: ({ node, ...props }) => (
                                      <ul
                                        className="ml-5 list-disc font-['Inter'] font-normal leading-tight text-[#e9e5ea]"
                                        {...props}
                                      />
                                    ),
                                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                    ol: ({ node, ...props }) => (
                                      <ol
                                        className="ml-5 list-decimal font-['Inter'] font-normal leading-tight text-[#e9e5ea]"
                                        {...props}
                                      />
                                    ),
                                  }}
                                >
                                  {selectedFileDetails.summary}
                                </Markdown>
                                <div className="h-[16px]"></div>
                                {renderDuplicateDocumentsSection()}
                              </ScrollableDiv>
                            )}
                          </>
                        ) : (
                          <>
                            <section className="mb-4">
                              <SidePanelAddTool
                                toolName="Compute Summary"
                                toolIcon={SummaryIcon}
                                isButtonDisabled={isAddingSummaryTool}
                                onClick={handleAddSummaryTool}
                                toolLoaderText={'Computing Summary...'}
                              />
                            </section>
                            {renderDuplicateDocumentsSection()}
                          </>
                        )}
                      </div>
                      {/* Signatures Section */}
                      {checkForTool('MISSING_SIGNATURE') ? (
                        // Show signatories if tool has been run
                        selectedFileDetails.missingSignature !== null && !isAddingSignaturesTool ? (
                          <div
                            className={`bottom-0 w-full bg-container-default`}
                            data-testid="signature-status-section"
                            ref={signatureSectionRef}
                          >
                            <div
                              className={`${isSignatureSectionOpen && 'mb-4'} group mt-4 flex h-[44px] w-full cursor-pointer justify-between gap-4 rounded-[8px] border border-[#353336] bg-[#353336] px-4 py-3 hover:bg-[#353336]`}
                              onClick={() => setIsSignatureSectionOpen(!isSignatureSectionOpen)}
                            >
                              <div className="flex items-center gap-2">
                                <img src={MissingSignatureIcon} alt="signature" />
                                <span className="text-[14px] font-medium">Signature Status</span>
                              </div>
                              <div>
                                <img
                                  src={ChevronIcon}
                                  className={`${isSignatureSectionOpen ? '' : 'rotate-180'}`}
                                />
                              </div>
                            </div>
                            {isSignatureSectionOpen && (
                              <ScrollableDiv
                                containerStyle={`${selectedFileDetails.signatories.length > 2 ? 'min-h-[130px] h-[calc(100vh-800px)] max-h-[280px]' : 'min-h-fit'} py-2  scrollbar-thumb-[#403E40] pr-2 mb-2 bg-container-default`}
                              >
                                <div className="flex h-[15px] w-full justify-between pr-2 text-[12px] font-bold text-marveri-light-silver">
                                  <h3>REQUIRED SIGNATORIES</h3>
                                  <h3>STATUS</h3>
                                </div>
                                <div className="inline-flex w-full flex-col items-start justify-start gap-3  py-3">
                                  {selectedFileDetails.signatories.map((signatory, idx) => (
                                    <div
                                      key={idx + 'signatory'}
                                      className="inline-flex w-full items-start justify-between self-stretch"
                                    >
                                      <div className="font-['Inter'] text-sm font-medium text-[#e9e5ea]">
                                        {signatory.signatory}
                                      </div>
                                      <div className="flex items-start justify-start">
                                        <div
                                          className={`px-2.5 py-0.5 ${
                                            signatory.status ? 'bg-[#353335]' : 'bg-[#B3261E]'
                                          } flex items-center justify-center gap-2.5 rounded-[23px] ${
                                            signatory.page === 0
                                              ? 'cursor-not-allowed opacity-50'
                                              : 'cursor-pointer hover:opacity-80'
                                          }`}
                                          onClick={() => handleSignatoryClick(signatory)}
                                        >
                                          <div className="text-nowrap font-['Inter'] text-[10px] font-semibold leading-[14px] text-white">
                                            {signatory.status ? 'Signed' : 'Signature required'}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </ScrollableDiv>
                            )}
                          </div>
                        ) : (
                          <section className="py-3" ref={signatureSectionRef}>
                            <div className="mb-4 flex h-[36px] w-full items-center justify-center rounded-[4px] bg-N600 pr-2 text-center text-[14px] font-medium text-marveri-white">
                              Signature Status
                            </div>
                            <div className="flex items-center justify-center gap-2">
                              <LoadingSpinner />
                              <span className="text-[16px]">Reviewing Signatures...</span>
                            </div>
                          </section>
                        )
                      ) : (
                        // Show button to add signatures tool if it hasn't been run
                        <section className={`py-3`}>
                          <div className="top-0 mb-4 flex h-[36px] w-full items-center justify-center rounded-[4px] bg-N600 pr-2 text-center text-[14px] font-medium text-marveri-white">
                            Signature Status
                          </div>
                          <SidePanelAddTool
                            toolName="Verify Signatures"
                            toolIcon={MissingSignatureIcon}
                            toolLoaderText="Reviewing Signatures..."
                            isButtonDisabled={isAddingSignaturesTool}
                            onClick={handleAddSignaturesTool}
                          />
                        </section>
                      )}
                    </div>
                  </section>
                </div>
              </TabPanel>
              <TabPanel>
                <ReferencesTab
                  pdfInteraction={pdfInteraction}
                  hoveredHighlight={hoveredHighlight}
                  handleJumpToHighlight={handleJumpToHighlight}
                  handleReferenceMouseOver={handleReferenceMouseOver}
                />
              </TabPanel>
              <TabPanel>
                <DocumentAnalysisTab
                  pdfInteraction={pdfInteraction}
                  hoveredHighlight={hoveredHighlight}
                  handleJumpToHighlight={handleJumpToHighlight}
                />
              </TabPanel>
              <TabPanel>
                <textarea
                  className={`${pdfInteraction ? 'h-[calc(100vh-150px)]' : 'h-[calc(100vh-115px)]'} w-full resize-none rounded-lg bg-[#191719] px-4 py-[10px] text-[14px] font-medium text-N400 outline-none`}
                ></textarea>
              </TabPanel>
            </TabPanels>
          </TabGroup>
        </div>
      )}
    </>
  );
};
